/**
 * useShortcuts — bind a registry scope to handlers (design/ux/01_foundations.md
 * §4.4, WP-17).
 *
 *   useShortcuts("ACHAT_SAISIE", { VALIDER: onValider, ENREGISTRER: onSave },
 *                { suppressGlobal: dirty ? ["nav"] : [] });
 *
 * The bindings come from config/shortcuts.config.js (GLOBAL_SHORTCUTS for
 * "GLOBAL", SCREEN_SHORTCUTS[id] for a screen, or `entries` for a dialog's
 * own map); the handlers are keyed by the binding ids. ONE capture-phase
 * listener (installed once by the shell's KeyboardLayer) dispatches every key
 * through lib/shortcut-dispatcher.js, so the handlers, the F1 sheet and the
 * ActionBar read the same map. A component never adds its own window keydown
 * listener for a registry key.
 *
 * Options
 *   kind            "screen" (default) · "global" (id "GLOBAL") · "overlay"
 *                   (a dialog / drawer map: fires first, and alone while a
 *                   modal is open)
 *   entries         explicit bindings (dialogs; defaults from the registry)
 *   suppressGlobal  ["nav"] while the screen holds unsaved work (§4.3 rule 6)
 *   suppressMessage the sentence shown instead of navigating
 *   title           F1 sheet heading (defaults to SCREEN_SHORTCUTS[id].title)
 *   enabled         false = not registered (e.g. a closed dialog)
 *   isActive        () => boolean, e.g. « my dialog is the top one »
 *
 * Handlers are read at key time (a ref), so they may close over fresh state
 * without re-registering. A handler returning `false` leaves the key to the
 * browser; `{ run, disabled, disabledReason }` swallows the key of a disabled
 * verb and says why.
 */
import * as React from "react";
import { createShortcutRegistry, SCOPE_KINDS } from "@/lib/shortcut-dispatcher.js";
import { dialogDepth } from "@/lib/dialog.js";
import { POS_SHORTCUTS, SUPPRESS_GROUPS } from "@/config/shortcuts.config.js";
import { MODAL_OVERLAY_SELECTOR, NAV_SUPPRESSED_MESSAGE } from "@/config/ui.config.js";

/** True while a modal overlay is open (the dialog stack or any aria-modal dialog). */
export function isModalOverlayOpen() {
  if (dialogDepth() > 0) return true;
  if (typeof document === "undefined") return false;
  return Boolean(document.querySelector(MODAL_OVERLAY_SELECTOR));
}

/** Toast through the single ToastHost bridge (works outside React). */
export function notifyToast(message, type = "info") {
  if (typeof window === "undefined" || !message) return;
  window.dispatchEvent(new CustomEvent("pharmakon:toast", { detail: { message, type } }));
}

/** Layout effect in the browser (before the next key can arrive), effect on the server. */
export const useCommitEffect = typeof window !== "undefined" ? React.useLayoutEffect : React.useEffect;

/** The one registry of the pharmacy shell. */
export const shortcutRegistry = createShortcutRegistry({
  isModalOpen: isModalOverlayOpen,
  notify: notifyToast,
  navSuppressedMessage: NAV_SUPPRESSED_MESSAGE,
});

/**
 * Install the single capture-phase dispatcher. Idempotent per target: a
 * second install (StrictMode double effect, a remount) is a no-op.
 * @returns {() => void} uninstall
 */
const installed = new WeakMap();
export function installShortcutDispatcher(target = typeof window !== "undefined" ? window : null) {
  if (!target) return () => {};
  const existing = installed.get(target);
  if (existing) {
    existing.count += 1;
    return existing.release;
  }
  const onKeyDown = (e) => {
    shortcutRegistry.handleKeydown(e);
  };
  target.addEventListener("keydown", onKeyDown, true);
  const entry = {
    count: 1,
    release: () => {
      entry.count -= 1;
      if (entry.count > 0) return;
      target.removeEventListener("keydown", onKeyDown, true);
      installed.delete(target);
    },
  };
  installed.set(target, entry);
  return entry.release;
}

export function useShortcuts(scopeId, handlers, options = {}) {
  const {
    kind = scopeId === "GLOBAL" ? SCOPE_KINDS.GLOBAL : SCOPE_KINDS.SCREEN,
    entries,
    suppressGlobal,
    suppressMessage,
    title,
    enabled = true,
    isActive,
  } = options;
  const handlersRef = React.useRef(handlers);
  const suppressRef = React.useRef(suppressGlobal);
  const activeRef = React.useRef(isActive);
  // Refreshed after every commit (never written during render): a key read
  // the handlers of the last committed render.
  useCommitEffect(() => {
    handlersRef.current = handlers;
    suppressRef.current = suppressGlobal;
    activeRef.current = isActive;
  });

  React.useEffect(() => {
    if (!enabled) return undefined;
    return shortcutRegistry.register({
      id: scopeId,
      kind,
      entries,
      title,
      suppressMessage,
      getHandlers: () => handlersRef.current ?? {},
      getSuppress: () => suppressRef.current ?? [],
      isActive: () => (activeRef.current ? Boolean(activeRef.current()) : true),
    });
  }, [scopeId, kind, entries, title, suppressMessage, enabled]);
}

/**
 * A POS screen whose hand-written listener stays the owner of its keys
 * (POS_SHORTCUTS is byte-identical, 02_UX_DESIGN §2.6). The dispatcher leaves
 * the global keys that the POS map declares (F1, Échap) to the page, and
 * `suppressNav` (a sale in progress) turns Ctrl+K / Alt+M / Alt+1…9 into
 * « Terminez ou mettez en attente la vente en cours ».
 * @param {keyof typeof POS_SHORTCUTS} posScreen
 * @param {{ suppressNav?: boolean }} [opts]
 */
export function useLegacyShortcutScreen(posScreen, opts = {}) {
  const suppressRef = React.useRef(Boolean(opts.suppressNav));
  const suppressNav = Boolean(opts.suppressNav);
  useCommitEffect(() => {
    suppressRef.current = suppressNav;
  });
  React.useEffect(
    () =>
      shortcutRegistry.register({
        id: posScreen,
        kind: SCOPE_KINDS.LEGACY,
        legacyKeys: (POS_SHORTCUTS[posScreen] ?? []).map((s) => s.key),
        getSuppress: () => (suppressRef.current ? [SUPPRESS_GROUPS.NAV] : []),
      }),
    [posScreen],
  );
}

/** Re-render when scopes are registered or removed (F1 sheet). */
export function useShortcutRegistryVersion() {
  const [v, setV] = React.useState(0);
  React.useEffect(() => shortcutRegistry.subscribe(() => setV((x) => x + 1)), []);
  return v;
}

export default useShortcuts;
